/**
 * Subscribe Page
 * Landing page for QR code scans - attempts to open preferred podcast app
 * Shows all available apps so user can choose an alternative if needed
 * Remembers user's app preference for future visits
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

type AudioStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'unknown';

interface SubscribeProps {
  token: string;
  feedsUrl: string;
  playerUrl: string;
  apiUrl: string;
  queueItemId?: string;
}

interface PodcastApp {
  id: string;
  name: string;
  icon: React.ReactNode;
  platforms: ('ios' | 'android')[];
  deepLink: (feedUrl: string) => string;
}

// ============================================================================
// Podcast App Icons (Inline SVGs with Official Brand Colors)
// ============================================================================

const ApplePodcastsIcon = () => (
  <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ borderRadius: 7 }}>
    <rect width="32" height="32" rx="7" fill="url(#ap-grad)" />
    <defs>
      <linearGradient id="ap-grad" x1="16" y1="0" x2="16" y2="32" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F452FF" />
        <stop offset="1" stopColor="#832BC1" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="13" r="7" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="16" cy="12" r="2.5" fill="white" />
    <path d="M13.5 22C13.5 19 14 17 16 17C18 17 18.5 19 18.5 22C18.5 23.5 18 24.5 16 24.5C14 24.5 13.5 23.5 13.5 22Z" fill="white" />
  </svg>
);

const OvercastIcon = () => (
  <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ borderRadius: 7 }}>
    <rect width="32" height="32" rx="7" fill="#FC7E0F" />
    <circle cx="16" cy="16" r="9" stroke="white" strokeWidth="1.8" fill="none" />
    <circle cx="16" cy="16" r="5.5" stroke="white" strokeWidth="1.8" fill="none" />
    <circle cx="16" cy="16" r="2" fill="white" />
    <line x1="10" y1="24" x2="13" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="22" y1="24" x2="19" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PocketCastsIcon = () => (
  <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ borderRadius: '50%' }}>
    <circle cx="16" cy="16" r="16" fill="#F43B24" />
    <circle cx="16" cy="16" r="10" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="16" cy="16" r="4" fill="white" />
    <rect x="15" y="4" width="2" height="4" rx="1" fill="white" />
    <rect x="15" y="24" width="2" height="4" rx="1" fill="white" />
    <rect x="4" y="15" width="4" height="2" rx="1" fill="white" />
    <rect x="24" y="15" width="4" height="2" rx="1" fill="white" />
  </svg>
);

const CastroIcon = () => (
  <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ borderRadius: 7 }}>
    <rect width="32" height="32" rx="7" fill="url(#castro-grad)" />
    <defs>
      <linearGradient id="castro-grad" x1="16" y1="0" x2="16" y2="32" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00CC6A" />
        <stop offset="1" stopColor="#00B359" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="14" r="8" stroke="white" strokeWidth="1.8" fill="none" />
    <circle cx="16" cy="14" r="4" stroke="white" strokeWidth="1.8" fill="none" />
    <circle cx="16" cy="14" r="1.5" fill="white" />
    <path d="M12 21L11 26H15L14 21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M20 21L21 26H17L18 21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const PodcastAddictIcon = () => (
  <svg width={32} height={32} viewBox="0 0 32 32" fill="none" style={{ borderRadius: 7 }}>
    <rect width="32" height="32" rx="7" fill="#1B74E4" />
    <path d="M10 13C10 9.686 12.686 7 16 7C19.314 7 22 9.686 22 13V17C22 20.314 19.314 23 16 23C12.686 23 10 20.314 10 17V13Z" stroke="white" strokeWidth="2" fill="none" />
    <line x1="8" y1="13" x2="10" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <line x1="22" y1="13" x2="24" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <circle cx="13.5" cy="14" r="1.5" fill="white" />
    <circle cx="18.5" cy="14" r="1.5" fill="white" />
    <path d="M13 18.5C13 18.5 14 20 16 20C18 20 19 18.5 19 18.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" />
  </svg>
);

// ============================================================================
// Preference Storage
// ============================================================================

const PREF_KEY = 'earpocket_preferred_app';

function getPreferredApp(): string | null {
  try {
    return localStorage.getItem(PREF_KEY);
  } catch {
    return null;
  }
}

function setPreferredApp(appName: string): void {
  try {
    localStorage.setItem(PREF_KEY, appName);
  } catch {
    // localStorage not available (e.g., private mode)
  }
}

// ============================================================================
// Podcast App Configurations
// ============================================================================

const PODCAST_APPS: PodcastApp[] = [
  {
    id: 'apple-podcasts',
    name: 'Apple Podcasts',
    icon: <ApplePodcastsIcon />,
    platforms: ['ios'],
    deepLink: (feedUrl) => `podcast://${feedUrl.replace('https://', '')}`,
  },
  {
    id: 'overcast',
    name: 'Overcast',
    icon: <OvercastIcon />,
    platforms: ['ios'],
    deepLink: (feedUrl) => `overcast://x-callback-url/add?url=${encodeURIComponent(feedUrl)}`,
  },
  {
    id: 'pocket-casts',
    name: 'Pocket Casts',
    icon: <PocketCastsIcon />,
    platforms: ['ios', 'android'],
    deepLink: (feedUrl) => `pktc://subscribe/${encodeURIComponent(feedUrl)}`,
  },
  {
    id: 'castro',
    name: 'Castro',
    icon: <CastroIcon />,
    platforms: ['ios'],
    deepLink: (feedUrl) => `castro://subscribe/${encodeURIComponent(feedUrl)}`,
  },
  {
    id: 'podcast-addict',
    name: 'Podcast Addict',
    icon: <PodcastAddictIcon />,
    platforms: ['android'],
    deepLink: (feedUrl) => `podcastaddict://subscribe/${encodeURIComponent(feedUrl)}`,
  },
];

// ============================================================================
// Platform Detection
// ============================================================================

function detectPlatform(): 'ios' | 'android' | 'desktop' {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

function getAvailableApps(): PodcastApp[] {
  const platform = detectPlatform();
  return PODCAST_APPS.filter((app) => {
    if (platform === 'ios') return app.platforms.includes('ios');
    if (platform === 'android') return app.platforms.includes('android');
    // On desktop, show all apps
    return true;
  });
}

// ============================================================================
// Deep Link Logic
// ============================================================================

/**
 * Attempt to open podcast app via deep link
 * Now accepts a deep link URL directly (not just feed URL)
 */
function attemptDeepLink(deepLinkUrl: string): void {
  window.location.href = deepLinkUrl;
}

/**
 * Get the app to try first based on user preference or default
 */
function getDefaultApp(availableApps: PodcastApp[]): PodcastApp {
  const preferredAppId = getPreferredApp();
  if (preferredAppId) {
    const preferredApp = availableApps.find(a => a.id === preferredAppId);
    if (preferredApp) {
      return preferredApp;
    }
  }
  // Default to first available app (Apple Podcasts on iOS)
  return availableApps[0];
}

// ============================================================================
// Subscribe Component
// ============================================================================

export function Subscribe({ token, feedsUrl, playerUrl, apiUrl, queueItemId }: SubscribeProps) {
  const [copied, setCopied] = useState(false);
  const [attemptedApp, setAttemptedApp] = useState<PodcastApp | null>(null);
  const [audioStatus, setAudioStatus] = useState<AudioStatus>(queueItemId ? 'unknown' : 'ready');
  const [progress, setProgress] = useState(0);
  const [deepLinked, setDeepLinked] = useState(false);
  const pollRef = useRef<number | null>(null);
  const progressRef = useRef<number | null>(null);
  const startTimeRef = useRef(Date.now());
  
  const feedUrl = `${feedsUrl}/${token}.xml`;
  const availableApps = getAvailableApps();
  const audioReady = audioStatus === 'ready';
  
  const pollAudioStatus = useCallback(async () => {
    if (!queueItemId) return;
    try {
      const res = await fetch(`${apiUrl}/v1/audio/queue/${queueItemId}/status`);
      const data = await res.json();
      if (!data.success) return;

      const status = data.audio?.status as AudioStatus;
      setAudioStatus(status);

      if (data.audio?.progress !== undefined) {
        setProgress(data.audio.progress);
      }

      if (status === 'ready' || status === 'failed') {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
      }
    } catch {
      // Silently retry on network error
    }
  }, [apiUrl, queueItemId]);

  // Poll audio status when we have a queueItemId
  useEffect(() => {
    if (!queueItemId) return;
    
    pollAudioStatus();
    pollRef.current = window.setInterval(pollAudioStatus, 2000);

    progressRef.current = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev;
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const simulated = Math.min(95, (elapsed / 60) * 100);
        return Math.max(prev, simulated);
      });
    }, 500);

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
    };
  }, [pollAudioStatus, queueItemId]);
  
  // Attempt deep link once audio is ready (only once)
  useEffect(() => {
    if (!audioReady || deepLinked) return;
    
    const apps = getAvailableApps();
    const defaultApp = getDefaultApp(apps);
    setAttemptedApp(defaultApp);
    setDeepLinked(true);
    
    const timer = setTimeout(() => {
      attemptDeepLink(defaultApp.deepLink(feedUrl));
    }, 100);
    
    return () => clearTimeout(timer);
  }, [audioReady, deepLinked, feedUrl]);
  
  function handleOpenInApp(app: PodcastApp) {
    setPreferredApp(app.id);
    setAttemptedApp(app);
    window.location.href = app.deepLink(feedUrl);
  }
  
  function handleListenInBrowser() {
    if (queueItemId) {
      window.location.href = `${playerUrl}/${queueItemId}`;
    } else {
      window.location.href = `${playerUrl}/subscribe/${token}`;
    }
  }
  
  async function handleCopyFeedUrl() {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = feedUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }
  
  const attemptedAppName = attemptedApp?.name || 'your podcast app';

  // Generating state: audio is not ready yet
  if (!audioReady && queueItemId) {
    return (
      <div className="container">
        <div className="player-card">
          <div className="header">
            <div className="logo">Earpocket</div>
            <h1 className="article-title">Preparing your audio...</h1>
            <p className="article-meta">This usually takes about a minute</p>
          </div>
          
          <div className="player-content">
            {audioStatus === 'failed' ? (
              <div className="status-container">
                <p className="status-text" style={{ color: '#ef4444' }}>Audio generation failed</p>
                <p className="status-subtext">Please try again from the extension</p>
              </div>
            ) : (
              <>
                <div className="status-container">
                  <div className="spinner" />
                  <p className="status-text">Generating audio...</p>
                  <p className="status-subtext">
                    We&apos;ll show your podcast app options once it&apos;s ready
                  </p>
                </div>

                <div className="progress-bar-container">
                  <div className="progress-bar-bg">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="subscribe-divider">
                  <span>or listen in the browser when ready</span>
                </div>
                
                <button 
                  className="secondary-button"
                  onClick={handleListenInBrowser}
                >
                  Open Web Player
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Audio is ready (or no queueItemId): show podcast app options
  return (
    <div className="container">
      <div className="player-card">
        <div className="header">
          <div className="logo">Earpocket</div>
          <h1 className="article-title">Opening {attemptedAppName}...</h1>
          <p className="article-meta">Subscribe to your personal audio feed</p>
        </div>
        
        <div className="player-content">
          <div className="status-container">
            <div className="spinner" />
            <p className="status-text">Opening {attemptedAppName}</p>
            <p className="status-subtext">Tap an app below if it doesn&apos;t open</p>
          </div>
          
          <div className="app-grid">
            {availableApps.map((app) => (
              <button 
                key={app.id}
                className={`app-icon-button ${attemptedApp?.id === app.id ? 'active' : ''}`}
                onClick={() => handleOpenInApp(app)}
                title={app.name}
              >
                <span className="app-icon">{app.icon}</span>
                <span className="app-label">{app.name}</span>
              </button>
            ))}
          </div>
          
          <div className="subscribe-divider">
            <span>or listen on the web</span>
          </div>
          
          <button 
            className="secondary-button"
            onClick={handleListenInBrowser}
          >
            Open Web Player
          </button>
          
          <details className="feed-url-details">
            <summary className="feed-url-summary">Copy feed URL manually</summary>
            <div className="feed-url-container">
              <code className="feed-url">{feedUrl}</code>
              <button 
                className="copy-button"
                onClick={handleCopyFeedUrl}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </details>
        </div>
        
        <div className="footer">
          <p className="podcast-cta">
            New episodes appear automatically in your podcast app
          </p>
        </div>
      </div>
    </div>
  );
}
